import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton, useToast } from '@/components/ui/workspace';
import { VendorConfidentialChip } from '@/components/ui/citation-chip';
import {
  errorMessage,
  usePatchSource,
  useKbSettings,
  useKbSource,
  useSourceAction,
  type KbProduct,
  type KbRun,
  type KbSourceDetail,
} from '@/lib/api/vendor-knowledge';
import { Actions, Chip, FormError, Linkish, smallControl } from '../kb-bits';
import { formatDate, money, outcomeText, plural } from '../kb-logic';
import { CaptureView, type PickedPassage } from '../capture/capture-view';
import { AddAsItemForm } from '../capture/add-as-item';
import { ReasonForm } from '../review/flags';

/**
 * One source, opened in place under its row (T-0207; E13-F04 §3.1): where
 * it is, how often it is checked, its versions with what changed and
 * whether each was read, the latest checks and readings, and the
 * curator's controls: Check now, Read now with the estimate the server
 * computes, the interval, View capture with Add as item, Retire or
 * Restore. Captured text is for curators only (rule 15).
 */
export function SourceDetail({ sourceId, products }: { sourceId: string; products: KbProduct[] }) {
  const detail = useKbSource(sourceId);
  if (detail.isPending) {
    return (
      <div className="border-t border-ink-100 bg-paper-50 px-4 py-3">
        <Spinner />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="border-t border-ink-100 bg-paper-50 px-4 py-3 text-meta text-posture-critical">
        {errorMessage(detail.error, 'This source could not be read.')}
      </div>
    );
  }
  return <DetailBody data={detail.data} products={products} />;
}

const INTERVALS = [30, 60, 90, 180];

function originText(origin: string): string {
  switch (origin) {
    case 'v1_import':
      return 'From the Caliber v1 import';
    case 'web_search':
      return 'Found by search and approved';
    default:
      return 'Added by a curator';
  }
}

function runText(r: KbRun): string {
  const counts = r.counts as Record<string, unknown>;
  const n = (k: string) => (typeof counts[k] === 'number' ? (counts[k] as number) : 0);
  const found = [
    n('proposed') > 0 ? `${plural(n('proposed'), 'passage')} proposed` : null,
    n('offered_citations') > 0
      ? `${plural(n('offered_citations'), 'further citation')} offered`
      : null,
    n('flagged') > 0 ? `${plural(n('flagged'), 'item')} flagged` : null,
  ].filter(Boolean);
  const what = r.kind === 'check' ? 'Check' : 'Reading';
  if (r.status === 'failed')
    return `${what} failed${r.error_code ? `: ${r.error_code.replace(/[._]/g, ' ')}` : ''}`;
  if (r.status !== 'done') return `${what} under way`;
  return `${what}: ${outcomeText(r.outcome)}${found.length ? `, ${found.join(', ')}` : ''}${r.cost_cents > 0 ? `. Used ${money(r.cost_cents)}` : ''}`;
}

function DetailBody({ data, products }: { data: KbSourceDetail; products: KbProduct[] }) {
  const { toast } = useToast();
  const settings = useKbSettings();
  const action = useSourceAction();
  const patch = usePatchSource();
  const s = data.source;
  const web = s.kind === 'web_page';
  const [viewing, setViewing] = React.useState<string | null>(null);
  const [picked, setPicked] = React.useState<PickedPassage | null>(null);
  const [mode, setMode] = React.useState<'retire' | 'unconfidential' | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const latest = data.versions[0] ?? null;
  const retired = s.status === 'retired';
  const firmDays = settings.data?.refresh_days;

  const run = (verb: 'check-now' | 'read-now' | 'restore', body?: Record<string, unknown>) => {
    setError(null);
    action.mutate(
      { id: s.id, verb, body },
      {
        onSuccess: (out) => {
          if (verb === 'check-now') {
            toast({
              message:
                out['created'] === false
                  ? 'A check of this page is already running. Its result appears here and under To review.'
                  : 'Checking now. An unchanged page costs nothing; what changed appears under To review.',
            });
          } else if (verb === 'read-now') {
            const cents = typeof out['estimate_cents'] === 'number' ? out['estimate_cents'] : null;
            toast({
              message: `Reading started${cents !== null ? `, estimated at ${money(cents)} of the firm AI budget` : ''}. Its proposals appear under To review.`,
            });
          } else {
            toast({ message: 'Restored. It is checked again on its schedule.' });
          }
        },
        onError: (err) => setError(errorMessage(err)),
      },
    );
  };

  const fact = 'text-meta font-medium leading-[22px] text-ink-500';
  return (
    <div className="border-t border-ink-100 bg-paper-50 px-4 pb-4 pt-3 sm:pl-[42px]">
      <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-1.5 text-body-sm sm:grid-cols-[140px_minmax(0,1fr)]">
        {s.url ? (
          <>
            <dt className={fact}>Address</dt>
            <dd className="m-0 break-all font-mono text-[11px] leading-[22px] text-ink-700">
              {latest?.final_url ?? s.url}
            </dd>
          </>
        ) : null}
        <dt className={fact}>Product</dt>
        <dd className="m-0">
          <select
            className={`${smallControl} pr-7`}
            aria-label="Product"
            disabled={retired || patch.isPending}
            value={s.product?.id ?? ''}
            onChange={(e) =>
              patch.mutate(
                { id: s.id, body: { product_id: e.target.value || null } },
                { onError: (err) => setError(errorMessage(err)) },
              )
            }
          >
            <option value="">All products</option>
            {products
              .filter((p) => p.status === 'active')
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
          </select>
        </dd>
        <dt className={fact}>Registered</dt>
        <dd className="m-0 leading-[22px] text-ink-700">
          {originText(s.origin)}, {formatDate(s.registered_at)}
          {s.outside_domain_reason ? ` · off the vendor’s domains: ${s.outside_domain_reason}` : ''}
        </dd>
        {web ? (
          <>
            <dt className={fact}>Check it every</dt>
            <dd className="m-0 flex flex-wrap items-center gap-2">
              <select
                className={`${smallControl} pr-7`}
                aria-label="How often to check this page"
                disabled={retired || patch.isPending}
                value={s.refresh_days ?? ''}
                onChange={(e) =>
                  patch.mutate(
                    {
                      id: s.id,
                      body: { refresh_days: e.target.value ? Number(e.target.value) : null },
                    },
                    {
                      onSuccess: () =>
                        toast({ message: 'Saved. The next check is set from the last one.' }),
                      onError: (err) => setError(errorMessage(err)),
                    },
                  )
                }
              >
                <option value="">
                  {firmDays ? `${firmDays} days (the firm’s setting)` : 'The firm’s setting'}
                </option>
                {INTERVALS.filter((d) => d !== firmDays).map((d) => (
                  <option key={d} value={d}>
                    {d} days
                  </option>
                ))}
                {s.refresh_days && !INTERVALS.includes(s.refresh_days) ? (
                  <option value={s.refresh_days}>{s.refresh_days} days</option>
                ) : null}
              </select>
              {s.next_check_at && !retired ? (
                <span className="text-meta text-ink-500">next {formatDate(s.next_check_at)}</span>
              ) : null}
            </dd>
          </>
        ) : null}
        {!web ? (
          <>
            <dt className={fact}>Vendor-confidential</dt>
            <dd className="m-0 flex flex-wrap items-center gap-2 leading-[22px]">
              {s.confidential ? (
                <VendorConfidentialChip />
              ) : (
                <span className="text-ink-700">No</span>
              )}
              {!retired ? (
                <Linkish
                  onClick={() =>
                    s.confidential
                      ? setMode('unconfidential')
                      : patch.mutate(
                          {
                            id: s.id,
                            body: { confidential: true, reason: 'Marked vendor-confidential' },
                          },
                          { onError: (err) => setError(errorMessage(err)) },
                        )
                  }
                >
                  {s.confidential ? 'Remove' : 'Mark vendor-confidential'}
                </Linkish>
              ) : null}
            </dd>
          </>
        ) : null}
      </dl>

      <div className="mt-3 text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
        Versions
      </div>
      {data.versions.length === 0 ? (
        <div className="mt-1 text-meta text-ink-500">
          {s.status === 'pending'
            ? 'Not captured yet: it waits until its domain is approved on the vendor’s header.'
            : 'Not captured yet.'}
        </div>
      ) : (
        <ul className="m-0 mt-1 list-none border-t border-ink-100 p-0">
          {data.versions.map((v) => (
            <li key={v.id} className="border-b border-ink-100 py-2">
              <div className="flex flex-wrap items-center gap-2 text-body-sm text-ink-900">
                <span className="font-medium">Version {v.version_no}</span>
                <span className="text-meta text-ink-500">
                  {web ? 'captured' : 'received'} {formatDate(v.captured_at)} ·{' '}
                  {plural(v.segment_count, 'passage')}
                  {v.version_no > 1 ? ` · ${v.changed_segments} changed` : ''}
                </span>
                {v.read ? (
                  <Chip tone="healthy">Read</Chip>
                ) : (
                  <Chip tone="unassessed">Not read</Chip>
                )}
                <span className="flex-1" />
                <Linkish
                  aria-expanded={viewing === v.id}
                  onClick={() => {
                    setViewing(viewing === v.id ? null : v.id);
                    setPicked(null);
                  }}
                >
                  {viewing === v.id ? 'Close capture' : 'View capture'}
                </Linkish>
              </div>
              {viewing === v.id ? (
                picked ? (
                  <AddAsItemForm
                    passage={picked}
                    products={products}
                    defaultProductId={s.product?.id ?? null}
                    onDone={() => setPicked(null)}
                    onCancel={() => setPicked(null)}
                  />
                ) : (
                  <CaptureView
                    versionId={v.id}
                    actionLabel="Add as item"
                    capturedAt={v.captured_at}
                    onPick={setPicked}
                  />
                )
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {data.runs.length > 0 ? (
        <>
          <div className="mt-3 text-[11px] font-medium uppercase leading-[18px] tracking-[0.06em] text-ink-500">
            Latest checks and readings
          </div>
          <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0">
            {data.runs.slice(0, 5).map((r) => (
              <li key={r.id} className="flex items-baseline gap-3 text-meta text-ink-700">
                <span className="min-w-0 flex-1">{runText(r)}</span>
                <span className="flex-none text-ink-500">
                  {formatDate(r.finished_at ?? r.created_at)}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {error ? <FormError>{error}</FormError> : null}
      {mode === 'retire' ? (
        <ReasonForm
          label="Why retire this source?"
          placeholder="For example: the vendor took the page down"
          help="Its items keep their citations. A retired source is not checked or read."
          button="Retire"
          destructive
          pending={action.isPending}
          onCancel={() => setMode(null)}
          onSubmit={(reason) =>
            action.mutate(
              { id: s.id, verb: 'retire', body: { reason } },
              {
                onSuccess: () => {
                  toast({ message: 'Retired. It is no longer checked or read.' });
                  setMode(null);
                },
                onError: (err) => setError(errorMessage(err)),
              },
            )
          }
        />
      ) : mode === 'unconfidential' ? (
        <ReasonForm
          label="Why is it no longer vendor-confidential?"
          help="Client pages and exports then show its quotes. This applies to every item from it at once."
          button="Remove vendor-confidential"
          pending={patch.isPending}
          onCancel={() => setMode(null)}
          onSubmit={(reason) =>
            patch.mutate(
              { id: s.id, body: { confidential: false, reason } },
              {
                onSuccess: () => setMode(null),
                onError: (err) => setError(errorMessage(err)),
              },
            )
          }
        />
      ) : (
        <Actions>
          {retired ? (
            <WorkspaceButton onClick={() => run('restore')} disabled={action.isPending}>
              Restore
            </WorkspaceButton>
          ) : (
            <>
              {web && s.status === 'active' ? (
                <WorkspaceButton onClick={() => run('check-now')} disabled={action.isPending}>
                  Check now
                </WorkspaceButton>
              ) : null}
              {latest && !latest.read && s.status === 'active' ? (
                <WorkspaceButton
                  onClick={() => run('read-now', { version_id: latest.id })}
                  disabled={action.isPending || s.active_run !== null}
                >
                  Read now
                </WorkspaceButton>
              ) : null}
              <WorkspaceButton variant="tertiary" onClick={() => setMode('retire')}>
                Retire
              </WorkspaceButton>
            </>
          )}
          {s.active_run ? (
            <span className="text-meta text-ink-500">
              {s.active_run.kind === 'check' ? 'A check' : 'A reading'} is under way.
            </span>
          ) : null}
        </Actions>
      )}
    </div>
  );
}
